/**
 * Вложения, версии активов и проверка материалов.
 *
 * Все три операции возвращают обновлённый список и заодно обновляют карточку
 * элемента: счётчик файлов и статус после проверки живут именно там.
 */

import type {
  AssetsDto,
  AssetVersionDto,
  AttachmentDto,
  ItemReviewDto,
} from '@project-os/contracts';
import type { CreateAssetVersionInput, CreateReviewInput } from '@project-os/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest, apiUpload } from './client';
import { itemKeys } from './items';
import { projectKeys } from './projects';

export const assetKeys = {
  list: (projectId: string) => ['projects', projectId, 'assets'] as const,
};

/** Реестр активов проекта. */
export function useAssets(projectId: string) {
  return useQuery({
    queryKey: assetKeys.list(projectId),
    queryFn: ({ signal }) => apiRequest<AssetsDto>(`/projects/${projectId}/assets`, { signal }),
  });
}

/** После любого изменения перечитываем карточку, список и журнал. */
function useItemScopedMutation<TInput, TResult>(
  projectId: string,
  itemId: string,
  request: (input: TInput) => Promise<TResult>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: itemKeys.detail(projectId, itemId) });
      void queryClient.invalidateQueries({ queryKey: itemKeys.all(projectId) });
      void queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
      void queryClient.invalidateQueries({ queryKey: assetKeys.list(projectId) });
    },
  });
}

export function useUploadAttachment(projectId: string, itemId: string) {
  return useItemScopedMutation(projectId, itemId, (file: File) =>
    apiUpload<AttachmentDto[]>(`/projects/${projectId}/items/${itemId}/attachments`, file),
  );
}

export function useDeleteAttachment(projectId: string, itemId: string) {
  return useItemScopedMutation(projectId, itemId, (attachmentId: string) =>
    apiRequest<AttachmentDto[]>(`/projects/${projectId}/attachments/${attachmentId}`, {
      method: 'DELETE',
    }),
  );
}

export function useCreateAssetVersion(projectId: string, itemId: string) {
  return useItemScopedMutation(projectId, itemId, (input: CreateAssetVersionInput) =>
    apiRequest<AssetVersionDto[]>(`/projects/${projectId}/items/${itemId}/versions`, {
      method: 'POST',
      body: input,
    }),
  );
}

/** Сделать версию текущей: старая остаётся в истории. */
export function useMakeVersionCurrent(projectId: string, itemId: string) {
  return useItemScopedMutation(projectId, itemId, (versionId: string) =>
    apiRequest<AssetVersionDto[]>(`/projects/${projectId}/versions/${versionId}`, {
      method: 'PATCH',
      body: { makeCurrent: true },
    }),
  );
}

export function useReviewItem(projectId: string, itemId: string) {
  return useItemScopedMutation(projectId, itemId, (input: CreateReviewInput) =>
    apiRequest<ItemReviewDto[]>(`/projects/${projectId}/items/${itemId}/reviews`, {
      method: 'POST',
      body: input,
    }),
  );
}
